/**
 * Get a human-readable description for WebSocket close codes
 * https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
 */
function getCloseCodeDescription(code: number): string {
  const descriptions: Record<number, string> = {
    1000: "Normal closure",
    1001: "Going away (server shutting down)",
    1002: "Protocol error",
    1003: "Unsupported data",
    1005: "No status received",
    1006: "Abnormal closure (connection lost)",
    1007: "Invalid data",
    1008: "Policy violation",
    1009: "Message too big",
    1010: "Missing extension",
    1011: "Internal server error",
    1012: "Service restart",
    1013: "Try again later",
    1014: "Bad gateway",
    1015: "TLS handshake failure",
  };
  return descriptions[code] ?? `Unknown close code`;
}

/**
 * Extract a meaningful error message from WebSocket errors.
 * WebSocket errors can be:
 * - CloseEvent: has code, reason, wasClean properties
 * - Event: generic DOM event with just type info
 * - Error: standard JS Error object
 */
export function getWebSocketErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (
    error &&
    typeof error === "object" &&
    "code" in error &&
    "reason" in error
  ) {
    const closeEvent = error as { code: number; reason: string };
    const reason =
      closeEvent.reason || getCloseCodeDescription(closeEvent.code);
    return `Connection closed: ${reason} (code ${closeEvent.code})`;
  }

  if (error && typeof error === "object" && "type" in error) {
    const event = error as { type: string };
    if (event.type === "error") {
      return "Failed to connect to Replo Agent. Make sure the agent is running.";
    }
    return `Connection event: ${event.type}`;
  }

  return "An unknown connection error occurred";
}
